import {
  ClusterOutlined,
  EditOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, Tabs, Tooltip } from "antd";
import { Content } from "antd/lib/layout/layout";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useParams } from "react-router-dom";
import { GetPerfCycleTopLevelApi } from "../../api/perf-cycles/GetPerfCycleTopPlanApi";
import GetPlansDetailsApi from "../../api/plans/GetPlanDetailsApi";
import DashboardBreadcrumb from "../../components/layout/DashboardBreadcrumb";
import CascadePlanModal from "../../components/modal/CascadePlanModal";
import EditTopPlanModal from "../../components/modal/edit/EditTopPlanModal";
import SwitchTopPlanModal from "../../components/modal/SwitchTopPlanModal";
import PageLoader from "../../components/sub/PageLoader";
import TopPlanKpi from "../../components/top-plan/TopPlanKpi";
import TopPlanOverview from "../../components/top-plan/TopPlanOverview";
import TopPlanPlans from "../../components/top-plan/TopPlanPlans";
import TopPlanTasks from "../../components/top-plan/TopPlanTasks";
import PlansTree from "../../components/tree/PlansTree";
import { permissionAbility } from "../../helpers/permissionHelpers";
import { getPluralString } from "../../helpers/wordHelpers";

const { TabPane } = Tabs;

function TopPlan({planType}) {
  const [topPlan, setTopPlan] = useState();
  const [canStart, setCanStart] = useState(false);
  const [canShow, setCanShow] = useState(false);
  const [planDetails, setPlanDetails] = useState();
  const [editModal, setEditModal] = useState(false);
  const [cascade, setCascade] = useState(false);
  const [switchModal, setSwitchModal] = useState(false);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const topPlanState = useSelector((state) => state.topPlanState.value);
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;
  const isCycleCompleted = perfCycleState?.status === "Completed";
  const proHeight = document.body.clientHeight;
  let curLocation = useLocation();
  const { id } = useParams();

  useEffect(() => {
    getTopPlanDetails();
    getPlanData();
  }, [curLocation, perfCycleState]);

  async function getTopPlanDetails() {
    const apiResponse = await GetPerfCycleTopLevelApi(perfCycleState.id);
    if (apiResponse.success) {
      setTopPlan(apiResponse.data);
    }
  }

  async function getPlanData() {
    const apiResponse = await GetPlansDetailsApi(id, positionState?.id);
    if (apiResponse.message === "success") {
      setPlanDetails(apiResponse.plan);
      setCanShow(true);
    }
    setCanStart(true);
  }

  const right = (
    <>
      {permissionAbility(userLevel, "plans", "cascade") && (
        <Tooltip title={"Cascade"}>
          <Button onClick={() => setCascade(true)} className="mr-3">
            <ClusterOutlined />
          </Button>
        </Tooltip>
      )}
      {permissionAbility(userLevel, "plans", "edit") && (
        isCycleCompleted ? (
          <Tooltip title={"Performance cycle is completed"}>
            <Button disabled>
              <EditOutlined />
            </Button>
          </Tooltip>
        ) : (
          <Tooltip title={"Edit"}>
            <Button onClick={() => setEditModal(true)}>
              <EditOutlined />
            </Button>
          </Tooltip>
        )
      )}
    </>
  );


  const left = (
    <>
      <div className="flex items-baseline">
        {planType === "my-plans" ? (
          <Link to={`/my-plans`}>
            <div className="text-sm pri-font text-gray-400 hover:text-gray-500">
              {getPluralString(topPlanState)}
            </div>
          </Link>
        ) : (
          <Link to={`/top-plans`}>
            <div className="text-sm pri-font text-gray-400 hover:text-gray-500">
              {getPluralString(topPlanState)}
            </div>
          </Link>
        )}
        <div className="mx-1 text-lg">/</div>
        <div className="text-lg pri-font max-w-xs truncate">
          {planDetails?.title || "..."}
        </div>
      </div>
    </>
  );
  

  return (
    <>
      <Content style={{ backgroundColor: "#fff" }}>
        {canStart ? (
          canShow ? (
            <>
              <DashboardBreadcrumb left={left} right={right} />
              <div
                style={{
                  height: proHeight - 122,
                  overflow: "auto",
                }}
                className="custom-scroll"
              >
                <Tabs defaultActiveKey="1" centered>
                  <TabPane
                    tab={<div className="w-32 text-center">Overview</div>}
                    key="1"
                  >
                    <div className="px-6">
                      <TopPlanOverview data={planDetails} />
                    </div>
                  </TabPane>
                  {topPlan?.kpi && (
                    <TabPane
                      tab={<div className="w-32 text-center">KPIs</div>}
                      key="2"
                    >
                      <div className="px-6">
                        <TopPlanKpi data={planDetails} />
                      </div>
                    </TabPane>
                  )}
                  {topPlan?.tasks && (
                    <TabPane
                      tab={<div className="w-32 text-center">Tasks</div>}
                      key="3"
                    >
                      <div className="px-6">
                        <TopPlanTasks data={planDetails} />
                      </div>
                    </TabPane>
                  )}
                  <TabPane
                    tab={<div className="w-32 text-center">Plans</div>}
                    key="4"
                  >
                    <div className="px-6">
                      <TopPlanPlans data={planDetails} />
                    </div>
                  </TabPane>
                  <TabPane
                    tab={<div className="w-32 text-center">Hierarchy</div>}
                    key="5"
                  >
                    <div className="px-6">
                      {!planDetails.canEdit ? (
                        <PlansTree />
                      ) : (
                        <div
                          style={{ height: proHeight - 188 }}
                          className={"flex w-full items-center justify-center"}
                        >
                          <div className="text-center">
                            <div className="text-3xl text-gray-400">
                              <ClusterOutlined />
                            </div>
                            <div className="text-lg pri-font text-gray-400">
                              No child plans
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </TabPane>
                </Tabs>
              </div>
            </>
          ) : (
            <div
              style={{ height: proHeight - 64 }}
              className={"flex w-full items-center justify-center"}
            >
              <div className="text-center">
                <div className="text-3xl text-gray-400">
                  <WarningOutlined />
                </div>
                <div className="text-lg pri-font text-gray-400">
                  No {topPlanState} Found
                </div>
              </div>
            </div>
          )
        ) : (
          <PageLoader />
        )}
      </Content>
      <SwitchTopPlanModal
        visible={switchModal}
        handleCancel={() => setSwitchModal(false)}
      />
      <CascadePlanModal
        visible={cascade}
        handleCancel={() => setCascade(false)}
        plan={planDetails}
      />
      <EditTopPlanModal
        visible={editModal}
        handleCancel={() => setEditModal(false)}
        editPlan={planDetails}
        handleOk={() => getPlanData()}
      />
    </>
  );
}

export default TopPlan;
